import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import type { UserConfig } from 'api/user/models';
import { INVESTMENT_OFFICES } from 'constants/globals';
import { loadThemeMode, saveThemeMode } from 'core/storage/themeModeStorage';
import type { GlobalAppType } from './types/global-app-store';
import type { RootState } from './configureStore';

const initialTheme = loadThemeMode();

const initialState: GlobalAppType = {
  logoApp: null,
  themeMode: initialTheme,
  isDark: initialTheme === 'dark',
  fullscreenTable: false,
  tableConfigs: [],
  investmentOfficeId: 'DMOS',
};

export const slice = createSlice({
  name: 'globalApp',
  initialState: { ...initialState },
  reducers: {
    setLogoApp: (global: GlobalAppType, action: PayloadAction<string>) => {
      global.logoApp = action.payload;
    },
    setTableConfigs: (
      global: GlobalAppType,
      action: PayloadAction<UserConfig[]>,
    ) => {
      global.tableConfigs = action.payload;
    },
    setFullscreenTable: (
      global: GlobalAppType,
      action: PayloadAction<boolean>,
    ) => {
      global.fullscreenTable = action.payload;
    },
    setThemeMode: (
      global: GlobalAppType,
      action: PayloadAction<'light' | 'dark'>,
    ) => {
      global.themeMode = action.payload;
      global.isDark = action.payload === 'dark';
      saveThemeMode(action.payload);
    },
    setInvestmentOfficeId: (
      global: GlobalAppType,
      action: PayloadAction<keyof typeof INVESTMENT_OFFICES>,
    ) => {
      global.investmentOfficeId = action.payload;
    },
    cleanData: () => initialState,
  },
});

export const {
  setLogoApp,
  cleanData,
  setThemeMode,
  setFullscreenTable,
  setTableConfigs,
  setInvestmentOfficeId,
} = slice.actions;

export default slice.reducer;

// Selectors
export const getLogoApp = (state: RootState) =>
  state.entities.globalApp.logoApp;
export const getThemeMode = (state: RootState) =>
  state.entities.globalApp.themeMode;
export const getIsDark = (state: RootState) => state.entities.globalApp.isDark;
export const getFullscreenTable = (state: RootState) =>
  state.entities.globalApp.fullscreenTable;
export const getTableConfigs = (state: RootState) =>
  state.entities.globalApp.tableConfigs;
export const getInvestmentOfficeId = (state: RootState) =>
  state.entities.globalApp.investmentOfficeId;
